import _ from "lodash";
import { useCallback, useLayoutEffect, useRef, useState } from "react";
import useLatestValue from "./useLatestValue";

const getDimensions = ({ entry, node }) => {
  const borderBoxSize = _.get(entry, "borderBoxSize[0]");
  // Polyfill where borderBoxSize is undefined(in one of the instances of Safari)
  if (!borderBoxSize) {
    const clientRect = (entry.target || node)?.getBoundingClientRect() || {};
    return { width: clientRect.width, height: clientRect.height };
  }
  return {
    width: borderBoxSize.inlineSize,
    height: borderBoxSize.height,
  };
};

// useResize is a utility tool to get the dimensions of particular node
// without using getBoundingClientRect() which is slow on performance
const useResize = (options = { throttle: false, throttleInterval: 200 }) => {
  const [dimensions, setDimensions] = useState({
    width: 0,
    height: 0,
  });

  const latestOptionsRef = useLatestValue(options);
  const observer = useRef();

  useLayoutEffect(() => {
    return () => {
      // disconnect on unmount
      if (observer.current) observer.current.disconnect();
    };
  }, []);

  const refHandler = useCallback(
    node => {
      // On node change, disconnect previous version of observer to prevent
      // memory leaks, and we could not use unobserve method, as we do not have any implicit
      // access to the previous node, which was passed to this function last time.
      if (observer.current) observer.current.disconnect();

      const { throttle, throttleInterval } = latestOptionsRef.current;
      const handleResize = entries => {
        const [container] = entries;
        setDimensions(getDimensions({ entry: container, node }));
      };

      const resizeCallback = throttle
        ? _.throttle(handleResize, throttleInterval)
        : handleResize;

      if (node) {
        observer.current = new ResizeObserver(resizeCallback);
        observer.current.observe(node, { box: "border-box" });
      }
    },
    [latestOptionsRef]
  );

  return [refHandler, dimensions];
};

export default useResize;
